import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { Check, Circle } from 'lucide-react';
import { C } from '../theme';

const API = process.env.REACT_APP_API_URL || '';

function StrengthChecklist({ password }) {
  const rules = [
    { label: 'At least 8 characters', ok: password.length >= 8 },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'One number', ok: /[0-9]/.test(password) },
    { label: 'One special character', ok: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];
  return (
    <div style={{ marginTop: 8, marginBottom: 4 }}>
      {rules.map(r => (
        <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          {r.ok
            ? <Check size={13} color="#2a8a5a" strokeWidth={2.5} />
            : <Circle size={13} color="#c0d0e0" strokeWidth={2} />}
          <span style={{ fontSize: 12, color: r.ok ? '#2a8a5a' : '#8aa0b8' }}>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const token = localStorage.getItem('plan_token');
  const { showToast } = useToast();

  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleChangePassword(e) {
    e.preventDefault();
    setError('');
    if (newPw !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: current, new_password: newPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update password');
      showToast('Password updated', 'success');
      setCurrent(''); setNewPw(''); setConfirm('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const inpStyle = {
    width: '100%', padding: '10px 12px',
    background: '#fff', border: '1px solid #dce8f0',
    borderRadius: 8, color: '#1a2a3a', fontSize: 14,
    outline: 'none', boxSizing: 'border-box', transition: 'border .15s',
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 520 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#1a2a3a', marginBottom: 6 }}>Settings</div>
      <div style={{ fontSize: 13, color: '#8aa0b8', marginBottom: 28 }}>{user?.email}</div>

      <div style={{ background: '#fff', border: '1px solid #dce8f0', borderRadius: 12, padding: '24px 28px' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2a3a', marginBottom: 20 }}>Change Password</div>
        <form onSubmit={handleChangePassword}>
          <div style={{ marginBottom: 16 }}>
            <label style={C.label}>Current password</label>
            <input type="password" value={current} onChange={e => setCurrent(e.target.value)} required style={inpStyle}
              onFocus={e => e.target.style.borderColor = '#0090d0'}
              onBlur={e => e.target.style.borderColor = '#dce8f0'}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={C.label}>New password</label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required style={inpStyle}
              onFocus={e => e.target.style.borderColor = '#0090d0'}
              onBlur={e => e.target.style.borderColor = '#dce8f0'}
            />
            {newPw && <StrengthChecklist password={newPw} />}
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={C.label}>Confirm new password</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required style={inpStyle}
              onFocus={e => e.target.style.borderColor = '#0090d0'}
              onBlur={e => e.target.style.borderColor = '#dce8f0'}
            />
          </div>
          {error && <div style={{ ...C.feedback('danger'), marginBottom: 12 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{
            padding: '10px 24px',
            background: loading ? '#c0d0e0' : 'linear-gradient(135deg, #0090d0, #0060a0)',
            border: 'none', borderRadius: 8, color: '#fff',
            fontWeight: 700, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 16px rgba(0,120,200,0.2)', transition: 'all .15s',
          }}>
            {loading ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
